/**
 * ReplayControls — date picker + driver selector for history replay mode.
 * Shown above the map so staff can replay a driver's route on a given date.
 */
import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/Button/Button';
import type { DriverOption } from '../hooks/useActiveDrivers';
import styles from './ReplayControls.module.css';

export interface ReplayControlsProps {
  /** Active drivers feeding the selector (from useActiveDrivers). */
  availableDrivers: DriverOption[];
  onReplay: (encDriverId: string, date: string) => void;
  onClearReplay: () => void;
  isReplaying: boolean;
}

export function ReplayControls({
  availableDrivers,
  onReplay,
  onClearReplay,
  isReplaying,
}: ReplayControlsProps) {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedDate, setSelectedDate] = useState(today);

  useEffect(() => {
    const first = availableDrivers[0];
    if (first && !selectedDriver) {
      setSelectedDriver(first.enc_id);
    }
  }, [availableDrivers, selectedDriver]);

  return (
    <div className={styles.controls}>
      <select
        value={selectedDriver}
        onChange={(e) => setSelectedDriver(e.target.value)}
        className={styles.select}
        aria-label="Select driver"
      >
        {availableDrivers.map((d) => (
          <option key={d.enc_id} value={d.enc_id}>
            {d.driver_name}
          </option>
        ))}
      </select>
      <input
        type="date"
        value={selectedDate}
        max={today}
        onChange={(e) => setSelectedDate(e.target.value)}
        className={styles.dateInput}
        aria-label="Select date"
      />
      <Button
        variant="primary"
        size="sm"
        onClick={() => onReplay(selectedDriver, selectedDate)}
        disabled={!selectedDriver}
      >
        View Route
      </Button>
      {isReplaying && (
        <Button variant="secondary" size="sm" onClick={onClearReplay}>
          Back to Live
        </Button>
      )}
    </div>
  );
}
